<template>
  <div class="op-editor-form">
    <section v-if="!draft.id" class="op-editor-section">
      <div class="op-editor-section-head">
        <div>
          <h3>Origem do credor</h3>
          <p>Localize o credor no QGC ou informe os dados manualmente quando ele ainda não estiver cadastrado.</p>
        </div>
      </div>

      <div class="party-source-grid">
        <button type="button" class="party-source-card" :class="{ active: draft._party_source === 'qgc' }" @click="setSource('qgc')">
          <span class="party-source-icon"><v-icon small>mdi-database-search-outline</v-icon></span>
          <span><strong>Credor do QGC</strong><small>Pesquise por nome ou CPF/CNPJ.</small></span>
          <v-icon small>{{ draft._party_source === 'qgc' ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
        </button>
        <button type="button" class="party-source-card" :class="{ active: draft._party_source === 'manual' }" @click="setSource('manual')">
          <span class="party-source-icon"><v-icon small>mdi-account-plus-outline</v-icon></span>
          <span><strong>Novo credor</strong><small>Informe nome e CPF/CNPJ.</small></span>
          <v-icon small>{{ draft._party_source === 'manual' ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
        </button>
      </div>
    </section>

    <section class="op-editor-section">
      <div class="op-editor-section-head">
        <div>
          <h3>{{ draft._party_source === 'qgc' && !draft.id ? 'Selecionar credor' : 'Identificação da parte' }}</h3>
          <p>{{ draft._party_source === 'qgc' && !draft.id ? 'A seleção utiliza o cadastro existente e não cria outro registro no QGC.' : 'Informe os dados cadastrais e o papel exercido no processo.' }}</p>
        </div>
      </div>

      <template v-if="draft._party_source === 'qgc' && !draft.id">
        <div class="op-field">
          <label>Credor do QGC <em>*</em></label>
          <v-autocomplete
            v-model="draft._qgc_creditor_id"
            :items="creditors"
            item-value="qgc_creditor_id"
            item-text="display"
            :loading="loadingCreditors"
            placeholder="Busque por nome ou CPF/CNPJ"
            no-data-text="Nenhum credor localizado no QGC"
            outlined
            dense
            clearable
            no-filter
            hide-details="auto"
            @change="selectCreditor"
            @update:search-input="search"
          >
            <template v-slot:item="{ item }">
              <div class="party-creditor-option">
                <span class="party-creditor-avatar">{{ initials(item.name) }}</span>
                <span><strong>{{ item.name }}</strong><small>{{ formatRegistration(item.registration) }} · {{ item.qgc_summary }}</small></span>
              </div>
            </template>
          </v-autocomplete>
        </div>
        <div v-if="draft._qgc_creditor_id" class="party-selected-summary">
          <v-icon small>mdi-check-circle-outline</v-icon>
          <div><strong>{{ draft.name }}</strong><small>{{ formatRegistration(draft.registration) }} · cadastro localizado no QGC</small></div>
        </div>
        <button v-else type="button" class="party-manual-link" @click="setSource('manual')">Não encontrou? Informar um novo credor</button>
      </template>

      <v-row v-else dense>
        <v-col cols="12" md="8"><div class="op-field"><label>Nome <em>*</em></label><v-text-field v-model="draft.name" placeholder="Informe o nome completo" outlined dense hide-details="auto" /></div></v-col>
        <v-col cols="12" md="4"><div class="op-field"><label>CPF/CNPJ <em>*</em></label><v-text-field v-model="draft.registration" v-mask="(draft.registration || '').length < 15 ? $masks.cpf : $masks.cnpj" placeholder="Informe o CPF ou CNPJ" required :rules="[$rules.required, validateRegistration]" outlined dense hide-details="auto" @blur="inferPersonType" /></div></v-col>
      </v-row>

      <v-row dense class="mt-1">
        <v-col cols="12" md="4"><div class="op-field"><label>Tipo</label><v-select v-model="draft.person_type" :items="personTypes" item-value="id" item-text="name" placeholder="Selecione" outlined dense hide-details="auto" /></div></v-col>
        <v-col cols="12" md="8"><div class="op-field"><label>Papel no processo <em>*</em></label><v-text-field v-model="draft.role" placeholder="Ex.: Credor" outlined dense hide-details="auto" /></div></v-col>
        <v-col cols="12"><div class="op-editor-check"><v-checkbox v-model="draft.is_debtor" hide-details><template v-slot:label><div><strong>Parte devedora</strong><small>Marque quando esta parte ocupar o polo devedor do processo.</small></div></template></v-checkbox></div></v-col>
        <v-col cols="12"><div class="op-field"><label>Observações <span>Opcional</span></label><v-textarea v-model="draft.notes" placeholder="Registre informações adicionais" rows="2" outlined hide-details="auto" /></div></v-col>
      </v-row>
    </section>

    <section v-if="!draft.id" class="op-editor-section">
      <div class="op-editor-section-head">
        <div>
          <h3>Crédito deste processo</h3>
          <p>O QGC pode ser usado somente para localizar nome e CPF/CNPJ. O valor e a classe abaixo são próprios deste processo e não alteram o consolidado.</p>
        </div>
      </div>

      <div class="op-editor-check">
        <v-checkbox v-model="draft.manual_credit_enabled" hide-details>
          <template v-slot:label>
            <div><strong>Informar valor e classe próprios</strong><small>Ative para registrar o crédito considerado especificamente neste processo.</small></div>
          </template>
        </v-checkbox>
      </div>

      <v-expand-transition>
        <v-row v-if="draft.manual_credit_enabled" dense class="mt-2">
          <v-col cols="12" md="5"><div class="op-field"><label>Valor <em>*</em></label><v-text-field v-model="draft.manual_credit_amount" type="number" min="0.01" step="0.01" placeholder="0,00" outlined dense hide-details="auto" :rules="[$rules.required, positiveAmount]" /></div></v-col>
          <v-col cols="12" md="3"><div class="op-field"><label>Moeda <em>*</em></label><v-select v-model="draft.manual_credit_currency" :items="currencies" item-value="id" item-text="name" placeholder="Selecione" outlined dense hide-details="auto" :rules="[$rules.required]" /></div></v-col>
          <v-col cols="12" md="4"><div class="op-field"><label>Classe <em>*</em></label><v-select v-model="draft.manual_credit_class" :items="creditClasses" item-value="id" item-text="name" placeholder="Selecione a classe" outlined dense hide-details="auto" :rules="[$rules.required]" /></div></v-col>
        </v-row>
      </v-expand-transition>
    </section>
  </div>
</template>

<script>
import creditorTypes from "@/common/data/creditor-types.data";

export default {
  name: "OtherProceedingPartyEditor",
  props: {
    value: { type: Object, required: true },
    creditors: { type: Array, default: () => [] },
    loadingCreditors: { type: Boolean, default: false },
  },
  data: () => ({
    searchTimer: null,
    personTypes: [
      { id: "individual", name: "Pessoa física" },
      { id: "company", name: "Pessoa jurídica" },
      { id: "public_body", name: "Órgão público" },
      { id: "other", name: "Outro" },
    ],
    creditClasses: creditorTypes,
    currencies: [
      { id: 1, name: "BRL (R$)" },
      { id: 2, name: "USD (US$)" },
      { id: 3, name: "EUR (€)" },
    ],
  }),
  computed: {
    draft: {
      get() { return this.value; },
      set(value) { this.$emit("input", value); },
    },
  },
  beforeDestroy() { clearTimeout(this.searchTimer); },
  methods: {
    digits(value) { return String(value || "").replace(/\D/g, ""); },
    initials(name) { return String(name || "").trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase() || "CR"; },
    formatRegistration(value) {
      const digits = this.digits(value);
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "Sem CPF/CNPJ";
    },
    validateRegistration(value) {
      const digits = this.digits(value);
      if (digits.length === 11) return this.$rules.cpf(value);
      if (digits.length === 14) return this.$rules.cnpj(value);
      return "O CPF/CNPJ deve ter 11 ou 14 dígitos.";
    },
    positiveAmount(value) {
      return Number(value) > 0 || "Informe um valor maior que zero.";
    },
    setSource(source) {
      if (this.draft._party_source === source) return;
      this.draft._party_source = source;
      this.draft._qgc_creditor_id = null;
      this.draft.name = "";
      this.draft.registration = "";
      this.draft.user_id = null;
      this.draft.person_type = "individual";
    },
    selectCreditor(id) {
      clearTimeout(this.searchTimer);
      const creditor = this.creditors.find(item => Number(item.qgc_creditor_id) === Number(id));
      if (!creditor) {
        this.draft.name = "";
        this.draft.registration = "";
        this.draft.user_id = null;
        return;
      }
      this.draft.name = creditor.name;
      this.draft.registration = creditor.registration;
      this.draft.user_id = creditor.user_id || null;
      this.draft.person_type = creditor.person_type || (this.digits(creditor.registration).length > 11 ? "company" : "individual");
    },
    inferPersonType() {
      const length = this.digits(this.draft.registration).length;
      if (length === 11) this.draft.person_type = "individual";
      if (length === 14) this.draft.person_type = "company";
    },
    search(value) {
      clearTimeout(this.searchTimer);
      const term = String(value || "").trim();
      if (term.length < 2) {
        this.$emit("search-creditors", "");
        return;
      }
      const selected = this.creditors.find(item => Number(item.qgc_creditor_id) === Number(this.draft._qgc_creditor_id));
      if (selected && [selected.display, selected.name, selected.registration].includes(term)) return;
      this.searchTimer = setTimeout(() => this.$emit("search-creditors", term), 300);
    },
  },
};
</script>

<style scoped>
.party-source-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.party-source-card { display: grid; grid-template-columns: 34px minmax(0, 1fr) 18px; gap: 10px; align-items: center; min-height: 66px; padding: 10px 12px; color: #334155; line-height: normal; text-align: left; background: #fff; border: 1px solid #dbe4ee; border-radius: 8px; transition: .15s ease; }
.party-source-card:hover { border-color: #9fb4c7; background: #f8fafc; }
.party-source-card.active { color: #014373; border-color: #014373; background: #f3f8fc; box-shadow: 0 0 0 2px rgba(1, 67, 115, .06); }
.party-source-card > span:nth-child(2) { align-self: center; min-width: 0; }
.party-source-card > .v-icon { display: flex; align-self: center; justify-self: center; align-items: center; justify-content: center; width: 18px; height: 18px; margin: 0; line-height: 1; }
.party-source-card strong, .party-source-card small { display: block; }
.party-source-card strong { font-size: 11px; font-weight: 700; }
.party-source-card small { margin-top: 3px; color: #718096; font-size: 9px; line-height: 1.35; }
.party-source-icon { display: flex; align-self: center; justify-self: center; align-items: center; justify-content: center; width: 32px; height: 32px; color: #014373; background: #eaf2f8; border-radius: 7px; }
.party-source-icon .v-icon { display: flex; align-items: center; justify-content: center; width: 18px; height: 18px; margin: 0; line-height: 1; }
.party-selected-summary { display: flex; align-items: center; gap: 9px; margin-top: 9px; padding: 9px 11px; color: #166534; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 7px; }
.party-selected-summary strong, .party-selected-summary small { display: block; }
.party-selected-summary strong { color: #1e293b; font-size: 10px; }
.party-selected-summary small { margin-top: 2px; color: #64748b; font-size: 9px; }
.party-manual-link { margin-top: 8px; color: #014373; font-size: 9px; font-weight: 700; }
@media (max-width: 600px) { .party-source-grid { grid-template-columns: 1fr; } }
</style>

<style>
/* O menu do v-autocomplete é renderizado fora do componente; por isso estas regras não podem ser scoped. */
.party-creditor-option { display: flex; align-items: center; gap: 10px; width: 100%; min-width: 0; min-height: 46px; padding: 4px 0; }
.party-creditor-option > span:last-child { min-width: 0; }
.party-creditor-option strong, .party-creditor-option small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.party-creditor-option strong { color: #1e293b; font-size: 11px; font-weight: 700; }
.party-creditor-option small { margin-top: 2px; color: #718096; font-size: 9px; }
.party-creditor-avatar { display: grid; flex: 0 0 30px; width: 30px; height: 30px; place-items: center; color: #014373; font-size: 9px; font-weight: 700; background: #eaf2f8; border-radius: 7px; }
</style>
